import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  fetchAuthorizedBranches,
  applyActiveBranch,
  applyAllBranches,
  rememberBranchChoice,
  lastBranchChoice,
} from '../utils/authorizedBranches';
import BranchPickDialog from './BranchPickDialog';
import { companyHasFeature } from '../utils/planFeatures';

/**
 * P8, branch at sign-in: a multi-branch user who has just logged in picks the
 * branch they will work in before anything else loads.
 *
 * The login reducers set sessionStorage['mm.pickBranchOnLogin'] and this gate,
 * mounted once in the authenticated shell, is its only reader:
 * - no flag (a refresh, a return visit) — render nothing, fetch nothing;
 * - at most one authorized branch, or the read fails — today's behaviour is
 *   already the home branch, so just drop the flag and stay out of the way;
 * - more than one — a non-dismissable BranchPickDialog (signin mode) whose
 *   pick applies the branch and reloads, so every surface refetches under it.
 *
 * The flag is removed before the reload, so the reloaded page cannot loop.
 * Super Admins are skipped: their branch list is every tenant's branches.
 */
const FLAG = 'mm.pickBranchOnLogin';

function readFlag() {
  try {
    return sessionStorage.getItem(FLAG) === '1';
  } catch (e) {
    return false;
  }
}

function clearFlag() {
  try {
    sessionStorage.removeItem(FLAG);
  } catch (e) {
    // Storage unavailable
  }
}

export default function SignInBranchGate() {
  const user = useSelector((state) => state.auth.user);
  const isSuperAdmin = user?.role?.name === 'Super Admin';
  const [pick, setPick] = useState(null); // { branches } once the dialog may show

  useEffect(() => {
    if (!readFlag()) return undefined;
    if (isSuperAdmin) {
      clearFlag();
      return undefined;
    }
    let alive = true;
    fetchAuthorizedBranches()
      .then((branches) => {
        if (!alive) return;
        if (!branches || branches.length <= 1) {
          clearFlag();
          return;
        }
        setPick({ branches });
      })
      .catch(() => {
        // Same behaviour as a single branch: the home branch, no dialog.
        clearFlag();
      });
    return () => {
      alive = false;
    };
  }, [isSuperAdmin]);

  if (!pick || isSuperAdmin) return null;

  const { branches } = pick;
  // /auth/me and both login bodies send all_branches_allowed (per_branch_roles
  // on): false for a user whose role differs between branches, so no "All
  // branches" for them. With the switch on a missing flag is NOT allowed
  // (fail closed); off, the flag is never sent and today's choice stays.
  const allowAll = companyHasFeature(user?.company, 'per_branch_roles')
    ? user?.all_branches_allowed === true
    : user?.all_branches_allowed !== false;
  // Last choice made on this device by this user, when it is still a branch
  // they may use ('all' is, unless refused above); otherwise the home branch,
  // else the first.
  const last = lastBranchChoice(user?.id);
  const defaultValue =
    (last === 'all' && allowAll) || branches.some((b) => b.id === last)
      ? last
      : (branches.find((b) => b.is_home) || branches[0])?.id;

  const handlePick = (choice) => {
    if (!pick) return; // one pick only
    setPick(null);
    rememberBranchChoice(user?.id, choice);
    if (choice === 'all') {
      applyAllBranches();
    } else {
      applyActiveBranch(choice);
    }
    // Flag goes first: the reloaded page must not see it again.
    clearFlag();
    window.location.reload();
  };

  return (
    <BranchPickDialog
      mode="signin"
      open
      branches={branches}
      defaultValue={defaultValue}
      onPick={handlePick}
      allowAll={allowAll}
      defaultRole={user?.default_role}
    />
  );
}
